import React, { type ReactNode, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTooltip } from '../../hooks/useTooltip';
import styles from './Tooltip.module.css';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: ReactNode;
  placement?: TooltipPlacement;
  delay?: number;
  offset?: number;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

export default function Tooltip({
  content,
  placement = 'top',
  delay = 500,
  offset = 8,
  disabled = false,
  className = '',
  children
}: TooltipProps) {
  const {
    triggerRef,
    tooltipRef,
    isVisible,
    placement: actualPlacement,
    position,
    arrowPosition,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    handleTouchStart,
    handleTouchEnd
  } = useTooltip({ placement, delay, offset, disabled });
  const tooltipId = useId();

  // Clone child element to add event handlers
  const trigger = React.cloneElement(children as React.ReactElement<any>, {
    ref: triggerRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    'aria-describedby': isVisible ? tooltipId : undefined
  });

  const tooltipElement = isVisible ? (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      className={`${styles.tooltip} ${styles[actualPlacement]} ${className}`}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex: 9999
      }}
    >
      <div className={styles.content}>
        {content}
      </div>
      <div
        className={`${styles.arrow} ${styles[`arrow-${actualPlacement}`]}`}
        style={{
          left: actualPlacement === 'top' || actualPlacement === 'bottom' ? arrowPosition.x : undefined,
          top: actualPlacement === 'left' || actualPlacement === 'right' ? arrowPosition.y : undefined
        }}
      />
    </div>
  ) : null;

  return (
    <>
      {trigger}
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
}

// Pre-built tooltip variants
export function HelpTooltip({
  content,
  placement = 'top',
  children
}: {
  content: ReactNode;
  placement?: TooltipPlacement;
  children: ReactNode;
}) {
  return (
    <Tooltip
      content={content}
      placement={placement}
      className={styles.help}
    >
      {children}
    </Tooltip>
  );
}

export function ErrorTooltip({
  content,
  placement = 'top',
  children
}: {
  content: ReactNode;
  placement?: TooltipPlacement;
  children: ReactNode;
}) {
  return (
    <Tooltip
      content={content}
      placement={placement}
      className={styles.error}
    >
      {children}
    </Tooltip>
  );
}

export function SuccessTooltip({
  content,
  placement = 'top',
  children
}: {
  content: ReactNode;
  placement?: TooltipPlacement;
  children: ReactNode;
}) {
  return (
    <Tooltip
      content={content}
      placement={placement}
      className={styles.success}
    >
      {children}
    </Tooltip>
  );
} 